<template>
  <CardExpandable
    v-if="taskUploadHasFolders(taskUpload)"
    content-cls="pa-0"
    title-cls="d-flex align-center pa-0"
  >
    <template v-if="taskUpload.folders" #title>
      <div class="text-body-large">
        {{ t('hints.n_folders', Object.keys(taskUpload.folders).length) }}
      </div>
      <v-spacer />
    </template>
    <template #content>
      <v-list
        class="overflow-y-auto"
        density="compact"
        style="max-height: 500px"
      >
        <DevTreeView :json="JSON.stringify({ taskUpload })" />
        <v-list-item
          v-for="folder in taskUpload.folders"
          :key="folder.id"
          class="py-0"
          prepend-icon="far fa-folder-arrow-up"
          slim
        >
          <template v-if="folder.data" #title>
            {{ folder.data.name }}
          </template>

          <v-list-item-subtitle
            v-if="folder.data && folder.status === 'error'"
            class="text-error"
          >
            {{ getTaskUploadErrorMsgFormatted(folder.data.error) }}
          </v-list-item-subtitle>
          <template #append>
            <v-icon
              v-if="folder.status === 'processing' || folder.status === 'added'"
              v-tooltip="
                t('upload.status.' + folder.status) + ` (${folder.progress} %)`
              "
              class="mr-1"
            >
              <v-progress-circular :model-value="folder.progress" width="2" />
            </v-icon>
            <v-icon
              v-else
              class="mr-1"
              v-bind="getTaskUploadStateIcon(folder.status)"
            ></v-icon>
          </template>
        </v-list-item>
      </v-list>
    </template>
  </CardExpandable>
  <v-divider
    v-if="taskUploadHasFolders(taskUpload) && taskUploadHasFiles(taskUpload)"
    class="ma-2"
  />
  <CardExpandable
    v-if="taskUploadHasFiles(taskUpload)"
    content-cls="pa-0"
    title-cls="d-flex align-center pa-0"
  >
    <template v-if="taskUpload.files" #title>
      <div class="text-body-large">
        {{ t('hints.n_files', taskUpload.files.length) }}
      </div>
      <v-spacer />
    </template>
    <template #content>
      <v-list
        class="overflow-y-auto"
        density="compact"
        style="max-height: 500px"
      >
        <DevTreeView :json="JSON.stringify({ taskUpload })" />
        <v-list-item
          v-for="file in taskUpload.files"
          :key="file.id"
          class="py-0"
        >
          <template #prepend>
            <v-avatar size="20" tile>
              <NodeIcon
                :mime-type="getFileIconNode(file)?.mimeType"
                :name="getFileIconNode(file)?.name"
                :type="getFileIconNode(file)?.type"
              />
            </v-avatar>
          </template>
          <template v-if="file.data" #title>
            <span v-if="file.data.name" data-test-id="filename">
              {{ file.data.name }}
            </span>
            <span v-else-if="file.data.file">
              {{ file.data.file.name }}
            </span>
          </template>
          <v-list-item-subtitle v-if="file.data">
            <span v-if="file.status === 'error'" class="text-error">
              {{ getTaskUploadErrorMsgFormatted(file.data.error) }}
            </span>
            <div v-else data-test-id="filesize">
              <span v-if="file.status === 'processing' && file.data.uploadSize">
                {{ humanFileSize(file.data.uploadSize?.uploaded) }}
                /
                {{ humanFileSize(file.data.uploadSize?.total) }}
              </span>
              <span v-else-if="file.data.file">
                {{ humanFileSize(file.data.file.size) }}
              </span>
            </div>
          </v-list-item-subtitle>

          <template #append>
            <v-icon
              v-if="file.status === 'processing' || file.status === 'added'"
              v-tooltip="
                t('upload.status.' + file.status) + ` (${file.progress} %)`
              "
              class="mr-1"
            >
              <v-progress-circular
                :indeterminate="file.status === 'processing' && !file.progress"
                :model-value="file.progress"
                width="2"
              ></v-progress-circular>
            </v-icon>
            <v-icon
              v-else
              v-tooltip="
                getTaskUploadStateIcon(file.status)?.title ?? undefined
              "
              class="mr-1"
              data-test-id="task-upload-status-icon"
              v-bind="getTaskUploadStateIcon(file.status)"
            />
          </template>
        </v-list-item>
      </v-list>
    </template>
  </CardExpandable>
</template>

<script setup lang="ts">
import { humanFileSize } from '@deepcloud/deep-ui-utils'
import CardExpandable from '@/components/cards/CardExpandable.vue'
import DevTreeView from '@/components/dev/DevTreeView.vue'
import NodeIcon from '@/components/node/NodeIcon.vue'
import { useTaskUpload } from '@/components/upload'
import type { TaskUploadNode } from '@/components/upload'
import type { TaskUpload } from '../types'

defineProps({
  taskUpload: {
    type: Object as PropType<TaskUpload>,
    default: () => ({}),
  },
})

const { t } = useI18n()

const {
  getTaskUploadStateIcon,
  getTaskUploadErrorMsgFormatted,
  taskUploadHasFolders,
  taskUploadHasFiles,
} = useTaskUpload()

function getFileIconNode(taskUploadNode: TaskUploadNode) {
  if (!taskUploadNode) return undefined

  if (taskUploadNode.status === 'completed') {
    return taskUploadNode.data?.node
  } else {
    return undefined
  }
}
</script>
